import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SidebarProvider } from "@/components/ui/sidebar";
import StudentHeader from "./StudentHeader";
import StudentSidebar from "./StudentSidebar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Calendar, Clock, BookOpen, Download, Printer, Loader2 } from "lucide-react";
import { db } from "../firebase/config";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";

interface Session {
  id: number;
  name: string;
  startTime: string;
  endTime: string;
  isBreak?: boolean;
}

interface Period {
  time: string;
  subject: string;
  teacher: string;
  room: string;
}

interface DaySchedule {
  day: string;
  periods: Period[];
}

interface StudentData {
  firstName: string;
  middleName?: string;
  lastName: string;
  registrationNumber: string;
  class: string;
  dormName: string;
  email: string;
  studentId: string;
  classId: string;
  dormId: string | null;
}

interface TimetableEntry {
  teacherId: string;
  teacherName: string;
  classId: string;
  className: string;
  subjectId: string;
  subjectName: string;
}

interface Subject {
  subjectId: string;
  name: string;
  code: string;
  department: string;
  type: "Core" | "Elective";
  assignedTeachers: number;
  teacherNames?: string[];
  description?: string;
  headOfDepartmentId?: string;
  schoolID: string;
  submittedBy: string;
  timestamp: string;
}

const StudentTimetable = () => {
  const [viewType, setViewType] = useState<'weekly' | 'daily'>('weekly');
  const [selectedDay, setSelectedDay] = useState('Monday');
  const [studentData, setStudentData] = useState<StudentData | null>(null);
  const [timetableData, setTimetableData] = useState<Record<string, TimetableEntry> | null>(null);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [availableClasses, setAvailableClasses] = useState<{id: string, name: string}[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  const [isLoadingStudent, setIsLoadingStudent] = useState(true);
  const [isLoadingTimetable, setIsLoadingTimetable] = useState(false);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState(true);
  const [isLoadingClasses, setIsLoadingClasses] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Auth / identity
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  
  const auth = getAuth();

  // Auth & Claims
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async user => {
      setIsAuthLoading(true);
      if (!user) { 
        setIsAuthLoading(false);
        return;
      }

      try {
        const idTokenResult = await user.getIdTokenResult();
        const claims = idTokenResult.claims as any;
        const sid = claims.schoolID || claims.defaultAffiliation?.schoolID || claims.affiliations?.[0]?.schoolID;

        if (sid) {
          setSchoolID(sid);
          setHasValidClaims(true);
        }
      } catch (e) {
        console.error("Error fetching auth data", e);
        setError("Authentication failed");
      } finally {
        setIsAuthLoading(false);
      }
    });
    return () => unsub();
  }, [auth]);

  // Fetch student data
  useEffect(() => {
    const fetchStudentData = async () => {
      if (!schoolID || !auth.currentUser?.email || !hasValidClaims) return;

      try {
        setIsLoadingStudent(true);
        const studentsRef = collection(db, 'schools', schoolID, 'students');
        const q = query(studentsRef, where('email', '==', auth.currentUser.email));
        const snap = await getDocs(q);
        
        if (!snap.empty) {
          const sData = { ...snap.docs[0].data(), studentId: snap.docs[0].id } as any;
          // Map fields to match interface
          const mappedData: StudentData = {
            firstName: sData.firstName,
            middleName: sData.middleName,
            lastName: sData.lastName,
            registrationNumber: sData.registrationNumber,
            class: sData.className || sData.class || '',
            dormName: sData.dormitory || '',
            email: sData.email,
            studentId: sData.studentId,
            classId: sData.currentClassUid || '',
            dormId: sData.dormId || null,
          };
          setStudentData(mappedData);
        } else {
          setError("Student data not found");
        }
      } catch (err) {
        console.error("Error fetching student data:", err);
        setError("Failed to load student data");
      } finally {
        setIsLoadingStudent(false);
      }
    };

    fetchStudentData();
  }, [schoolID, auth.currentUser?.email, hasValidClaims]);

  // Fetch timetable data
  useEffect(() => {
    const fetchTimetable = async () => {
      if (!schoolID || !hasValidClaims || !selectedClassId) return;

      try {
        setIsLoadingTimetable(true);
        const timetableRef = doc(db, 'schools', schoolID, 'timetablePublished', selectedClassId);
        const timetableSnap = await getDoc(timetableRef);
        
        if (timetableSnap.exists()) {
          const data = timetableSnap.data();
          setTimetableData(data.schedule || {});
        } else {
          setTimetableData({});
        }
      } catch (err) {
        console.error("Error fetching timetable:", err);
        setError("Failed to load timetable");
      } finally {
        setIsLoadingTimetable(false);
      }
    };

    fetchTimetable();
  }, [schoolID, selectedClassId, hasValidClaims]);

  // Fetch subjects data
  useEffect(() => {
    const fetchSubjects = async () => {
      if (!schoolID || !hasValidClaims) return;

      try {
        setIsLoadingSubjects(true);
        const subjectsRef = collection(db, 'schools', schoolID, 'subjects');
        const subjectsSnap = await getDocs(subjectsRef);
        
        const subjectsData = subjectsSnap.docs.map(doc => ({
          subjectId: doc.id,
          ...doc.data()
        })) as Subject[];
        
        setSubjects(subjectsData);
      } catch (err) {
        console.error("Error fetching subjects:", err);
        // Don't set error for subjects, just log it
      } finally {
        setIsLoadingSubjects(false);
      }
    };

    fetchSubjects();
  }, [schoolID, hasValidClaims]);

  // Fetch sessions
  useEffect(() => {
    const fetchSessions = async () => {
      if (!schoolID || !hasValidClaims) return;

      try {
        const sessionsRef = doc(db, 'schools', schoolID, 'config', 'sessionsPublished');
        const sessionsSnap = await getDoc(sessionsRef);
        
        if (sessionsSnap.exists()) {
          setSessions(sessionsSnap.data().sessions || []);
        } else {
          // Default sessions
          const defaultSessions = [
            { id: 1, name: "Period 1", startTime: "08:00", endTime: "09:00", isBreak: false },
            { id: 2, name: "Period 2", startTime: "09:00", endTime: "10:00", isBreak: false },
            { id: 3, name: "Break", startTime: "10:00", endTime: "10:30", isBreak: true },
            { id: 4, name: "Period 3", startTime: "10:30", endTime: "11:30", isBreak: false },
            { id: 5, name: "Period 4", startTime: "11:30", endTime: "12:30", isBreak: false },
            { id: 6, name: "Lunch", startTime: "12:30", endTime: "13:30", isBreak: true },
            { id: 7, name: "Period 5", startTime: "13:30", endTime: "14:30", isBreak: false },
            { id: 8, name: "Period 6", startTime: "14:30", endTime: "15:30", isBreak: false },
          ];
          setSessions(defaultSessions);
        }
      } catch (err) {
        console.error("Error fetching sessions:", err);
      }
    };

    fetchSessions();
  }, [schoolID, hasValidClaims]);

  // Fetch available classes
  useEffect(() => {
    const fetchClasses = async () => {
      if (!schoolID || !hasValidClaims) return;

      try {
        setIsLoadingClasses(true);
        const classesRef = collection(db, 'schools', schoolID, 'classes');
        const classesSnap = await getDocs(classesRef);
        
        const classesData = classesSnap.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || doc.data().className || `Class ${doc.id}`
        }));
        
        setAvailableClasses(classesData);
        
        // Set default selected class to student's class if available and not already set
        if (studentData?.classId && !selectedClassId) {
          setSelectedClassId(studentData.classId);
        }
      } catch (err) {
        console.error("Error fetching classes:", err);
        // Don't set error for classes, just log it
      } finally {
        setIsLoadingClasses(false);
      }
    };

    fetchClasses();
  }, [schoolID, hasValidClaims, studentData?.classId]);

  // Set default selected class when student data is loaded
  useEffect(() => {
    if (studentData?.classId && !selectedClassId && availableClasses.length > 0) {
      setSelectedClassId(studentData.classId);
    }
  }, [studentData?.classId, selectedClassId, availableClasses.length]);

  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

  // Transform Firebase timetable data to UI format
  const studentTimetable: DaySchedule[] = days.map(day => {
    const periods: Period[] = [];
    
    // Use sessions to generate periods
    const sortedSessions = [...sessions].sort((a, b) => a.id - b.id);

    sortedSessions.forEach(session => {
      const key = `${session.id}-${day}`;
      const entry = timetableData?.[key];
      
      if (entry) {
        periods.push({
          time: `${session.startTime} - ${session.endTime}`,
          subject: entry.subjectName,
          teacher: entry.teacherName,
          room: "Classroom"
        });
      } else {
        // If it's a break session, add it as a break period
        if (session.isBreak || session.name.toLowerCase().includes('break') || session.name.toLowerCase().includes('lunch')) {
             periods.push({
                time: `${session.startTime} - ${session.endTime}`,
                subject: "Break",
                teacher: "-",
                room: "-"
             });
        } else {
             // Empty slot
             periods.push({
                time: `${session.startTime} - ${session.endTime}`,
                subject: "", 
                teacher: "",
                room: ""
             });
        }
      }
    });
    
    return {
      day,
      periods
    };
  });

  const getNextClass = () => {
    const now = new Date();
    const currentDay = days[now.getDay() - 1];
    const currentTime = now.getHours() * 60 + now.getMinutes();

    const todaySchedule = studentTimetable.find(d => d.day === currentDay);
    if (!todaySchedule) return null;

    for (const period of todaySchedule.periods) {
      const [startHour, startMin] = period.time.split(' - ')[0].split(':').map(Number);
      const periodTime = startHour * 60 + startMin;
      
      if (periodTime > currentTime && period.subject !== "Break") {
        return { ...period, day: currentDay };
      }
    }
    return null;
  };

  const getSubjectColor = (subject: string) => {
    // Define color palettes for different subject types
    const colorPalettes = [
      { bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-300" },
      { bg: "bg-purple-100 dark:bg-purple-900/30", text: "text-purple-700 dark:text-purple-300" },
      { bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-300" },
      { bg: "bg-amber-100 dark:bg-amber-900/30", text: "text-amber-700 dark:text-amber-300" },
      { bg: "bg-teal-100 dark:bg-teal-900/30", text: "text-teal-700 dark:text-teal-300" },
      { bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-300" },
      { bg: "bg-pink-100 dark:bg-pink-900/30", text: "text-pink-700 dark:text-pink-300" },
      { bg: "bg-indigo-100 dark:bg-indigo-900/30", text: "text-indigo-700 dark:text-indigo-300" },
      { bg: "bg-cyan-100 dark:bg-cyan-900/30", text: "text-cyan-700 dark:text-cyan-300" },
      { bg: "bg-violet-100 dark:bg-violet-900/30", text: "text-violet-700 dark:text-violet-300" },
      { bg: "bg-slate-100 dark:bg-slate-900/30", text: "text-slate-700 dark:text-slate-300" },
      { bg: "bg-orange-100 dark:bg-orange-900/30", text: "text-orange-700 dark:text-orange-300" },
      { bg: "bg-lime-100 dark:bg-lime-900/30", text: "text-lime-700 dark:text-lime-300" },
      { bg: "bg-emerald-100 dark:bg-emerald-900/30", text: "text-emerald-700 dark:text-emerald-300" },
      { bg: "bg-rose-100 dark:bg-rose-900/30", text: "text-rose-700 dark:text-rose-300" },
    ];

    // Create a mapping based on actual subjects from database, excluding 'Break', 'Lunch', and empty
    const subjectColors: { [key: string]: { bg: string; text: string } } = {};
    subjects.forEach((subj, index) => {
      if (!subj.name || /break|lunch/i.test(subj.name)) return;
      const colorIndex = index % colorPalettes.length;
      subjectColors[subj.name] = colorPalettes[colorIndex];
    });
    return subjectColors[subject] || { bg: "bg-gray-100 dark:bg-gray-800/50", text: "text-gray-700 dark:text-gray-300" };
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    window.print();
  };

  const nextClass = getNextClass();

  // Auth loading state
  if (isAuthLoading) {
    return (
      <SidebarProvider>
        <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
          <StudentSidebar />
          <div className="flex flex-col flex-1 h-full">
            <StudentHeader />
            <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
              <div className="text-center">
                <Loader2 className="h-10 w-10 animate-spin text-blue-600 mx-auto mb-4" />
                <p className="text-sm text-gray-600">Loading authentication...</p>
              </div>
            </div>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  // Auth validation
  if (!hasValidClaims || !schoolID) {
    return (
      <SidebarProvider>
        <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
          <StudentSidebar />
          <div className="flex flex-col flex-1 h-full">
            <StudentHeader />
            <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
              <div className="text-center">
                <p className="text-lg text-red-600 mb-4">Invalid student credentials</p>
                <p className="text-sm text-gray-600">Please contact your administrator.</p>
              </div>
            </div>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  // Loading state
  if (isLoadingStudent) {
    return (
      <SidebarProvider>
        <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
          <StudentSidebar />
          <div className="flex flex-col flex-1 h-full">
            <StudentHeader />
            <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
              <div className="text-center">
                <Loader2 className="h-10 w-10 animate-spin text-blue-600 mx-auto mb-4" />
                <p className="text-sm text-gray-600">Loading student profile...</p>
              </div>
            </div>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  // Error state
  if (error) {
    return (
      <SidebarProvider>
        <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
          <StudentSidebar />
          <div className="flex flex-col flex-1 h-full">
            <StudentHeader />
            <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
              <div className="text-center">
                <p className="text-lg text-red-600 mb-4">{error}</p>
                <Button onClick={() => window.location.reload()}>
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  // No student data
  if (!studentData) {
    return (
      <SidebarProvider>
        <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
          <StudentSidebar />
          <div className="flex flex-col flex-1 h-full">
            <StudentHeader />
            <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
              <div className="text-center">
                <p className="text-lg text-gray-600">Student data not found</p>
              </div>
            </div>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
        <StudentSidebar />
        <div className="flex flex-col flex-1 h-full">
          <StudentHeader />
          <main className="flex-1 p-3 sm:p-6 overflow-auto bg-gray-50 dark:bg-gray-900">
            <div className="space-y-4 sm:space-y-6">
      {/* Print styles */}
      <style>{`
        @media print {
          @page {
            size: landscape;
            margin: 1cm;
            background: white;
          }
          
          body {
            background: white !important;
            background-color: white !important;
          }
          
          body * {
            visibility: hidden;
          }
          
          .print-content,
          .print-content * {
            visibility: visible;
          }
          
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 20px;
            background: white !important;
            background-color: white !important;
          }
          
          .no-print {
            display: none !important;
          }
          
          .print-title {
            font-family: 'Poppins', sans-serif !important;
            font-weight: 800 !important;
            font-size: 32px !important;
            background: linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%) !important;
            -webkit-background-clip: text !important;
            -webkit-text-fill-color: transparent !important;
            background-clip: text !important;
            margin-bottom: 8px !important;
          }
          
          .print-subtitle {
            font-family: 'Inter', sans-serif !important;
            font-size: 16px !important;
            color: #6B7280 !important;
            font-weight: 600 !important;
          }
          
          .print-time {
            font-family: 'Space Mono', monospace !important;
            font-weight: 700 !important;
            color: #3B82F6 !important;
          }
          
          .print-subject {
            font-family: 'Poppins', sans-serif !important;
            font-weight: 700 !important;
          }
          
          .print-header-cell {
            font-family: 'Poppins', sans-serif !important;
            font-weight: 700 !important;
            background: linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%) !important;
            color: #1E40AF !important;
          }
          
          .print-math { background-color: #DBEAFE !important; color: #1E40AF !important; }
          .print-english { background-color: #E9D5FF !important; color: #6B21A8 !important; }
          .print-science { background-color: #D1FAE5 !important; color: #065F46 !important; }
          .print-history { background-color: #FEF3C7 !important; color: #92400E !important; }
          .print-geography { background-color: #CCFBF1 !important; color: #115E59 !important; }
          .print-pe { background-color: #FEE2E2 !important; color: #991B1B !important; }
          .print-art { background-color: #FCE7F3 !important; color: #9F1239 !important; }
          .print-music { background-color: #E0E7FF !important; color: #3730A3 !important; }
          .print-computer { background-color: #CFFAFE !important; color: #155E75 !important; }
          .print-french { background-color: #EDE9FE !important; color: #5B21B6 !important; }
          .print-library { background-color: #F3F4F6 !important; color: #374151 !important; }
          .print-break { background-color: #F9FAFB !important; color: #9CA3AF !important; }
          
          table {
            page-break-inside: auto;
          }
          
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
        }
      `}</style>

      {/* Print content - enhanced with fonts and colors */}
      <div className="print-content hidden print:block">
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 className="print-title">
            My Class Schedule
          </h1>
          <p className="print-subtitle">Weekly Timetable</p>
        </div>
        
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          border: '3px solid #3B82F6',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          <thead>
            <tr>
              <th className="print-header-cell" style={{ 
                border: '2px solid #93C5FD', 
                padding: '14px', 
                textAlign: 'center',
                fontSize: '15px'
              }}>
                Time
              </th>
              {days.map((day, idx) => {
                const colors = [
                  { border: '#93C5FD', bg: 'linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)' },
                  { border: '#C4B5FD', bg: 'linear-gradient(135deg, #E9D5FF 0%, #DDD6FE 100%)' },
                  { border: '#F9A8D4', bg: 'linear-gradient(135deg, #FCE7F3 0%, #FBCFE8 100%)' },
                  { border: '#FED7AA', bg: 'linear-gradient(135deg, #FED7AA 0%, #FDBA74 100%)' },
                  { border: '#86EFAC', bg: 'linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)' },
                ];
                return (
                  <th key={day} className="print-header-cell" style={{ 
                    border: `2px solid ${colors[idx].border}`, 
                    padding: '14px', 
                    textAlign: 'center',
                    background: colors[idx].bg,
                    fontSize: '15px'
                  }}>
                    {day}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {studentTimetable[0].periods.map((_, periodIndex) => (
              <tr key={periodIndex}>
                <td className="print-time" style={{ 
                  border: '1px solid #93C5FD', 
                  padding: '12px', 
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  background: '#F0F9FF'
                }}>
                  {studentTimetable[0].periods[periodIndex].time}
                </td>
                {days.map(day => {
                  const daySchedule = studentTimetable.find(d => d.day === day);
                  const period = daySchedule?.periods[periodIndex];
                  
                  const getColorClass = (subject: string) => {
                    const map: { [key: string]: string } = {
                      "Mathematics": "print-math",
                      "English": "print-english",
                      "Science": "print-science",
                      "History": "print-history",
                      "Geography": "print-geography",
                      "Physical Education": "print-pe",
                      "Art": "print-art",
                      "Music": "print-music",
                      "Computer Science": "print-computer",
                      "French": "print-french",
                      "Library Period": "print-library",
                      "Break": "print-break",
                    };
                    return map[subject] || "print-library";
                  };
                  
                  return (
                    <td key={day} className={period ? getColorClass(period.subject) : ''} style={{ 
                      border: '1px solid #D1D5DB', 
                      padding: '12px',
                      fontSize: '12px',
                      minHeight: '70px'
                    }}>
                      {period ? (
                        <div>
                          <div className="print-subject" style={{ 
                            marginBottom: '6px',
                            fontSize: '14px'
                          }}>
                            {period.subject}
                          </div>
                          {period.subject !== "Break" && (
                            <>
                              <div style={{ 
                                fontSize: '11px',
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: '500',
                                marginBottom: '2px'
                              }}>
                                {period.teacher}
                              </div>
                              <div style={{ 
                                fontSize: '11px',
                                fontFamily: 'Inter, sans-serif',
                                opacity: '0.8'
                              }}>
                                {period.room}
                              </div>
                            </>
                          )}
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', color: '#9CA3AF' }}>-</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        
        <div style={{ 
          marginTop: '24px', 
          fontSize: '12px', 
          color: '#6B7280',
          textAlign: 'center',
          fontFamily: 'Inter, sans-serif',
          fontWeight: '500'
        }}>
          Printed on {new Date().toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </div>
      </div>

      {/* Header with export options */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 no-print">
        <div>
          <h2 className="text-xl sm:text-3xl font-heading font-bold flex items-center gap-2 bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
            <Calendar className="h-5 w-5 sm:h-7 sm:w-7 text-primary" />
            {availableClasses.find(c => c.id === selectedClassId)?.name || 'Class'} Schedule
            {isLoadingTimetable && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
          </h2>
          <p className="text-sm text-muted-foreground mt-1 font-medium">
            {isLoadingTimetable ? "Loading timetable..." : `View the weekly timetable for ${availableClasses.find(c => c.id === selectedClassId)?.name || 'selected class'}`}
          </p>
        </div>
        
        <div className="flex gap-2 w-full sm:w-auto">
          <Button variant="outline" size="sm" onClick={handlePrint} className="flex-1 sm:flex-none border-primary/20 hover:border-primary hover:bg-primary/5">
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadPDF} className="flex-1 sm:flex-none border-primary/20 hover:border-primary hover:bg-primary/5">
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Next Class Card */}
      {nextClass && (
        <Card className="no-print border-l-4 border-l-primary bg-gradient-to-r from-primary/10 via-purple-500/5 to-transparent">
          <CardHeader className="pb-3">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2 font-heading">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-primary animate-pulse" />
              Next Class
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <p className="font-heading font-bold text-base sm:text-xl text-foreground">{nextClass.subject}</p>
              <p className="text-sm text-muted-foreground font-medium">{nextClass.teacher} • {nextClass.room}</p>
              <p className="text-sm text-primary font-mono font-bold">{nextClass.time}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* View Options */}
      <Card className="mt-4 no-print border-primary/20 shadow-md">
        <CardHeader className="pb-3 bg-gradient-to-r from-primary/5 to-transparent">
          <CardTitle className="text-base sm:text-lg font-heading">View Options</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            {/* Class Selection Dropdown */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <Label htmlFor="class-select" className="text-sm font-medium text-foreground sm:min-w-[120px]">
                Select Class:
              </Label>
              <Select value={selectedClassId} onValueChange={setSelectedClassId} disabled={isLoadingClasses}>
                <SelectTrigger className="mt-2 w-full sm:w-[200px] bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31]">
                  <SelectValue placeholder={isLoadingClasses ? "Loading classes..." : "Choose a class"} />
                </SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                  {availableClasses.map((classItem) => (
                    <SelectItem 
                      key={classItem.id} 
                      value={classItem.id}
                      className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]"
                    >
                      {classItem.name}
                    </SelectItem>
                  ))}
                  {availableClasses.length === 0 && (
                    <SelectItem value="no-classes" disabled className="font-medium text-black dark:text-white">
                      No classes available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Tabs value={viewType} onValueChange={(v) => setViewType(v as 'weekly' | 'daily')} className="w-full">
                <TabsList className="mt-2 w-full sm:w-auto bg-muted/50">
                  <TabsTrigger value="weekly" className="flex-1 sm:flex-none font-medium">Weekly View</TabsTrigger>
                  <TabsTrigger value="daily" className="flex-1 sm:flex-none font-medium">Daily View</TabsTrigger>
                </TabsList>
              </Tabs>

              {viewType === 'daily' && (
                <Tabs value={selectedDay} onValueChange={setSelectedDay} className="w-full sm:w-auto">
                  <TabsList className="w-full grid grid-cols-5 sm:inline-flex bg-muted/50">
                    {days.map(day => (
                      <TabsTrigger key={day} value={day} className="text-xs sm:text-sm px-2 sm:px-3 font-medium">
                        {day.substring(0, 3)}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timetable Display */}
      {isLoadingTimetable ? (
        <Card className="border-primary/20 shadow-lg">
          <CardContent className="p-12 flex justify-center items-center">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-sm text-muted-foreground">Loading your timetable...</p>
            </div>
          </CardContent>
        </Card>
      ) : !timetableData || Object.keys(timetableData).length === 0 ? (
        <Card className="border-primary/20 shadow-lg">
          <CardContent className="p-12 flex justify-center items-center">
            <div className="text-center">
              <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium text-muted-foreground mb-2">No Timetable for {availableClasses.find(c => c.id === selectedClassId)?.name || selectedClassId || 'selected class'} Available</p>
              <p className="text-sm text-muted-foreground">Your class timetable hasn't been published yet. Please contact your administrator.</p>
            </div>
          </CardContent>
        </Card>
      ) : viewType === 'weekly' ? (
        <Card className="border-primary/20 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-primary/10 via-purple-500/5 to-transparent border-b border-primary/10">
            <CardTitle className="text-base sm:text-xl flex items-center gap-2 font-heading">
              <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              {availableClasses.find(c => c.id === selectedClassId)?.name || 'Class'} Schedule
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm font-medium">All classes for the week</CardDescription>
          </CardHeader>
          <CardContent className="p-0 sm:p-6">
            {/* Mobile Layout - Days as separate sections */}
            <div className="block sm:hidden">
              <div className="space-y-4 p-4">
                {days.map((day, dayIdx) => {
                  const daySchedule = studentTimetable.find(d => d.day === day);
                  const gradients = [
                    'from-blue-500/10 to-blue-600/5',
                    'from-purple-500/10 to-purple-600/5',
                    'from-pink-500/10 to-pink-600/5',
                    'from-orange-500/10 to-orange-600/5',
                    'from-green-500/10 to-green-600/5',
                  ];

                  return (
                    <div key={day} className={`rounded-lg border border-border/30 overflow-hidden bg-gradient-to-br ${gradients[dayIdx]}`}>
                      <div className="bg-primary/10 px-3 py-2 border-b border-border/20">
                        <h3 className="font-heading font-bold text-sm text-foreground">{day}</h3>
                      </div>
                      <div className="p-3 space-y-2">
                        {daySchedule?.periods.map((period, periodIndex) => {
                          const matchingSession = sessions.find(s => {
                            const periodTimes = period.time.split(' - ');
                            return s.startTime === periodTimes[0].trim() && s.endTime === periodTimes[1].trim();
                          });
                          const isBreak = matchingSession?.isBreak || matchingSession?.name.toLowerCase().includes('break') || matchingSession?.name.toLowerCase().includes('lunch');

                          if (isBreak) {
                            return (
                              <div key={periodIndex} className="w-full p-3 bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg">
                                <div className="flex items-center justify-center gap-2">
                                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                    <Clock className="w-4 h-4" />
                                    <span>{matchingSession?.name || 'Break'}</span>
                                    <span className="text-xs">({period.time})</span>
                                  </div>
                                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                                </div>
                              </div>
                            );
                          }

                          const colors = getSubjectColor(period.subject);
                          return (
                            <div key={periodIndex} className={`p-3 rounded-lg ${colors.bg} border border-border/30`}>
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0">
                                  <p className="text-xs font-mono font-bold text-primary/70 bg-background/50 px-2 py-1 rounded">
                                    {period.time}
                                  </p>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`font-heading font-bold text-sm ${colors.text} truncate`}>
                                    {period.subject}
                                  </p>
                                  <div className="mt-1 space-y-1">
                                    <p className="text-xs text-muted-foreground font-medium truncate">{period.teacher}</p>
                                    <p className="text-xs text-muted-foreground truncate">{period.room}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Tablet Layout - Compact Grid */}
            <div className="hidden sm:block lg:hidden overflow-x-auto">
              <div className="min-w-full">
                <div className="grid grid-cols-6 gap-1 sm:gap-2 p-2 sm:p-0">
                  {/* Header - Time column */}
                  <div className="font-mono font-bold text-xs text-primary/70 px-1 sm:px-2 bg-primary/5 rounded-lg flex items-center justify-center">Time</div>

                  {/* Header - Day columns */}
                  {days.map((day, idx) => {
                    const gradients = [
                      'from-blue-500/10 to-blue-600/5',
                      'from-purple-500/10 to-purple-600/5',
                      'from-pink-500/10 to-pink-600/5',
                      'from-orange-500/10 to-orange-600/5',
                      'from-green-500/10 to-green-600/5',
                    ];
                    return (
                      <div key={day} className={`font-heading font-bold text-xs text-center text-foreground bg-gradient-to-br ${gradients[idx]} rounded-lg py-1 sm:py-2`}>
                        <span className="hidden sm:inline">{day}</span>
                        <span className="sm:hidden">{day.substring(0, 3)}</span>
                      </div>
                    );
                  })}

                  {/* Periods */}
                  {studentTimetable[0].periods.map((_, periodIndex) => {
                    const firstPeriod = studentTimetable[0].periods[periodIndex];
                    const matchingSession = sessions.find(s => {
                      if (!firstPeriod) return false;
                      const periodTimes = firstPeriod.time.split(' - ');
                      return s.startTime === periodTimes[0].trim() && s.endTime === periodTimes[1].trim();
                    });
                    const isBreak = matchingSession?.isBreak || matchingSession?.name.toLowerCase().includes('break') || matchingSession?.name.toLowerCase().includes('lunch');

                    if (isBreak) {
                      return (
                        <div key={`period-${periodIndex}`} className="contents">
                          <div className="col-span-6 w-full mb-2 p-3 bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg">
                            <div className="flex items-center justify-center gap-3">
                              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                <Clock className="w-4 h-4" />
                                <span>{matchingSession?.name || 'Break'}</span>
                                <span className="text-xs">({firstPeriod.time})</span>
                              </div>
                              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return (
                    <div key={`period-${periodIndex}`} className="contents">
                      {/* Time cell */}
                      <div className="text-xs font-mono font-bold text-primary/70 py-1 sm:py-2 px-1 sm:px-2 flex items-center justify-center bg-primary/5 rounded-lg">
                        {studentTimetable[0].periods[periodIndex].time}
                      </div>

                      {/* Day cells */}
                      {days.map(day => {
                        const daySchedule = studentTimetable.find(d => d.day === day);
                        const period = daySchedule?.periods[periodIndex];

                        const colors = period ? getSubjectColor(period.subject) : { bg: "", text: "" };

                        return (
                          <div key={`${day}-${periodIndex}`} className={`p-1 sm:p-2 rounded-lg ${colors.bg} min-h-[50px] sm:min-h-[80px] border border-border/30 hover:shadow-md transition-shadow`}>
                            {period && (
                              <div className="space-y-0.5">
                                <p className={`font-heading font-bold text-xs ${colors.text} truncate`}>
                                  {period.subject}
                                </p>
                                <p className="text-xs text-muted-foreground truncate font-medium hidden sm:block">{period.teacher}</p>
                                <p className="text-xs text-muted-foreground truncate hidden sm:block">{period.room}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )})}
                </div>
              </div>
            </div>

            {/* Desktop Layout - Full Grid */}
            <div className="hidden lg:block overflow-x-auto">
              <div className="min-w-full lg:min-w-[800px]">
                <div className="grid grid-cols-6 gap-1 lg:gap-2 p-2 lg:p-0">
                  {/* Header - Time column */}
                  <div className="font-mono font-bold text-xs text-primary/70 px-1 lg:px-2 bg-primary/5 rounded-lg flex items-center justify-center">Time</div>

                  {/* Header - Day columns */}
                  {days.map((day, idx) => {
                    const gradients = [
                      'from-blue-500/10 to-blue-600/5',
                      'from-purple-500/10 to-purple-600/5',
                      'from-pink-500/10 to-pink-600/5',
                      'from-orange-500/10 to-orange-600/5',
                      'from-green-500/10 to-green-600/5',
                    ];
                    return (
                      <div key={day} className={`font-heading font-bold text-xs text-center text-foreground bg-gradient-to-br ${gradients[idx]} rounded-lg py-1 lg:py-2`}>
                        <span className="hidden lg:inline">{day}</span>
                        <span className="lg:hidden">{day.substring(0, 3)}</span>
                      </div>
                    );
                  })}

                  {/* Periods */}
                  {studentTimetable[0].periods.map((_, periodIndex) => {
                    const firstPeriod = studentTimetable[0].periods[periodIndex];
                    const matchingSession = sessions.find(s => {
                      if (!firstPeriod) return false;
                      const periodTimes = firstPeriod.time.split(' - ');
                      return s.startTime === periodTimes[0].trim() && s.endTime === periodTimes[1].trim();
                    });
                    const isBreak = matchingSession?.isBreak || matchingSession?.name.toLowerCase().includes('break') || matchingSession?.name.toLowerCase().includes('lunch');

                    if (isBreak) {
                      return (
                        <div key={`period-${periodIndex}`} className="contents">
                          <div className="col-span-6 w-full mb-2 p-3 bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg">
                            <div className="flex items-center justify-center gap-3">
                              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                <Clock className="w-4 h-4" />
                                <span>{matchingSession?.name || 'Break'}</span>
                                <span className="text-xs">({firstPeriod.time})</span>
                              </div>
                              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return (
                    <div key={`period-${periodIndex}`} className="contents">
                      {/* Time cell */}
                      <div className="text-xs font-mono font-bold text-primary/70 py-1 lg:py-2 px-1 lg:px-2 flex items-center justify-center bg-primary/5 rounded-lg">
                        {studentTimetable[0].periods[periodIndex].time}
                      </div>

                      {/* Day cells */}
                      {days.map(day => {
                        const daySchedule = studentTimetable.find(d => d.day === day);
                        const period = daySchedule?.periods[periodIndex];

                        const colors = period ? getSubjectColor(period.subject) : { bg: "", text: "" };

                        return (
                          <div key={`${day}-${periodIndex}`} className={`p-1 lg:p-2 rounded-lg ${colors.bg} min-h-[50px] lg:min-h-[80px] border border-border/30 hover:shadow-md transition-shadow`}>
                            {period && (
                              <div className="space-y-0.5">
                                <p className={`font-heading font-bold text-xs ${colors.text} truncate`}>
                                  {period.subject}
                                </p>
                                <p className="text-xs text-muted-foreground truncate font-medium hidden lg:block">{period.teacher}</p>
                                <p className="text-xs text-muted-foreground truncate hidden lg:block">{period.room}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )})}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-primary/20 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-primary/10 via-purple-500/5 to-transparent border-b border-primary/10">
            <CardTitle className="text-base sm:text-xl flex items-center gap-2 font-heading">
              <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              {selectedDay}'s Schedule - {availableClasses.find(c => c.id === selectedClassId)?.name || 'Class'}
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm font-medium">Classes for {selectedDay}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 sm:space-y-6">
              {studentTimetable.find(d => d.day === selectedDay)?.periods.map((period, index) => {
                // Check if this period is a break by matching time with sessions
                const matchingSession = sessions.find(s => {
                  const periodTimes = period.time.split(' - ');
                  return s.startTime === periodTimes[0].trim() && s.endTime === periodTimes[1].trim();
                });
                const isBreak = matchingSession?.isBreak || matchingSession?.name.toLowerCase().includes('break') || matchingSession?.name.toLowerCase().includes('lunch');
                
                // Render break sessions as horizontal banners
                if (isBreak) {
                  return (
                    <div key={index} className="w-full p-3 bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg">
                      <div className="flex items-center justify-center gap-2">
                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          <span>{matchingSession?.name || 'Break'}</span>
                          <span className="text-xs">({period.time})</span>
                        </div>
                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                      </div>
                    </div>
                  );
                }
                
                const colors = getSubjectColor(period.subject);
                return (
                  <div key={index} className={`p-3 sm:p-4 rounded-lg ${colors.bg} flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 border border-border/30 hover:shadow-md transition-all`}>
                    <div className="flex-shrink-0">
                      <p className="text-xs sm:text-sm font-mono font-bold text-primary/70 bg-background/50 px-2 py-1 rounded">{period.time}</p>
                    </div>
                    <div className="flex-1">
                      <p className={`font-heading font-bold text-sm sm:text-lg ${colors.text}`}>{period.subject}</p>
                      <div className="flex flex-col sm:flex-row sm:gap-4 mt-1">
                        <p className="text-xs sm:text-sm text-muted-foreground font-medium">{period.teacher}</p>
                        <p className="text-xs sm:text-sm text-muted-foreground">{period.room}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subject Color Legend */}
      <Card className="no-print border-primary/20">
        <CardHeader className="bg-gradient-to-r from-primary/5 to-transparent">
          <CardTitle className="text-base sm:text-lg font-heading">Subject Colors</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoadingSubjects ? (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span className="ml-2 text-sm text-muted-foreground">Loading subjects...</span>
            </div>
          ) : subjects.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">No subjects available</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
              {/* Actual subjects from database, excluding Break/Lunch/empty */}
              {subjects.filter(subject => subject.name && !/break|lunch/i.test(subject.name)).map((subject) => {
                const colors = getSubjectColor(subject.name);
                return (
                  <div key={subject.subjectId} className="m-2 flex items-center gap-2 bg-muted/30 px-2 py-1 rounded-lg hover:bg-muted/50 transition-colors">
                    <div className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full ${colors.bg} border border-border/30`} />
                    <span className="text-xs sm:text-sm text-foreground font-medium truncate" title={subject.name}>
                      {subject.name}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default StudentTimetable;